import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Moon, Sun, Anchor, ChevronDown } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { useTheme } from "@/context/ThemeContext";
import { BRAND, SERVICES } from "@/data/content";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Plans" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/get-app", label: "Get the App" },
];

export const Navbar = () => {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const onHome = location.pathname === "/";
  const transparent = onHome && !scrolled && !open;

  const barClass = transparent
    ? "bg-transparent"
    : "glass-nav bg-navy-900/85 border-b border-white/10";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${barClass}`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
            <BrandMark className="h-11 w-11" />
            <span className="leading-tight">
              <span className="block font-serif text-lg sm:text-xl font-semibold text-white tracking-wide">
                Hussar Marine
              </span>
              <span className="block eyebrow text-gold-400 -mt-0.5">Concierge</span>
            </span>
          </Link>

          {/* desktop */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.slice(0, 1).map((l) => (
              <NavItem key={l.to} {...l} />
            ))}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-sm font-medium text-slate-200 hover:text-gold-400 transition-colors"
                data-testid="nav-services-trigger"
              >
                Services <ChevronDown className="h-4 w-4" />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-72">
                  <div className="glass-nav bg-navy-800/95 border border-gold-500/20 rounded-xl p-2 shadow-2xl">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className="block px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                        data-testid={`nav-service-${s.slug}`}
                      >
                        <span className="block text-sm font-semibold text-white">{s.name}</span>
                        <span className="block text-xs text-slate-400 mt-0.5">{s.tagline}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {navLinks.slice(1).map((l) => (
              <NavItem key={l.to} {...l} />
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-200 hover:text-gold-400 hover:border-gold-500/40 transition-colors"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={() => navigate("/request-inspection")}
              className="hidden sm:inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold text-sm px-5 py-2.5 rounded-full transition-all hover:shadow-[0_8px_24px_-8px_rgba(212,175,55,0.7)]"
              data-testid="nav-request-inspection-button"
            >
              <Anchor className="h-4 w-4" /> Request Inspection
            </button>
            <button
              className="lg:hidden h-10 w-10 inline-flex items-center justify-center text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden glass-nav bg-navy-900/97 border-t border-white/10 animate-fade-in">
          <nav className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-base font-medium text-slate-100 border-b border-white/5"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="py-2">
              <p className="eyebrow text-gold-400 pt-2 pb-1">Services</p>
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="block py-2.5 text-slate-200"
                  data-testid={`mobile-nav-service-${s.slug}`}
                >
                  {s.name}
                </Link>
              ))}
            </div>
            <button
              onClick={() => navigate("/request-inspection")}
              className="mt-3 inline-flex items-center justify-center gap-2 bg-gold-500 text-navy-900 font-semibold px-5 py-3 rounded-full"
              data-testid="mobile-request-inspection-button"
            >
              <Anchor className="h-4 w-4" /> Request Inspection
            </button>
            <button
              onClick={toggle}
              className="mt-2 inline-flex items-center justify-center gap-2 border border-white/15 text-slate-200 px-5 py-3 rounded-full"
            >
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

const NavItem = ({ to, label }) => (
  <NavLink
    to={to}
    end={to === "/"}
    className={({ isActive }) =>
      `text-sm font-medium transition-colors ${
        isActive ? "text-gold-400" : "text-slate-200 hover:text-gold-400"
      }`
    }
    data-testid={`nav-${label.toLowerCase()}`}
  >
    {label}
  </NavLink>
);

export default Navbar;
